import { Stack, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { ContainerJourneyStatus } from '@planning/components/molecules/ContainerJourneyStatusChip'
import { useOrderNavigation } from '@planning/hooks/useOrderNavigation'
import { IContainerJourneyItem } from '@planning/pages/Issues/Stores/ContainerJourneyItem'
import { adminActionService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { ButtonOption, ButtonWithDropdown, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { EditOrderPageViewStore } from '../../stores/EditOrderPageViewStore'

export const EditOrderPageHeader: FC<{
  editOrderPageViewStore: EditOrderPageViewStore
}> = observer(({ editOrderPageViewStore }) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const { authStore, appViewStore } = usePlanningStore()
  const isAdmin = authStore.isAdmin()

  const { navigateCheckoutContainerPage, navigateCheckinContainerVisitsPage } = useOrderNavigation()

  const onDeleteJourney = async (journey: IContainerJourneyItem) => {
    if (!journey) return

    const hasServiceOrders = journey.data.strippingOrderId || journey.data.stuffingOrderId
    const inboundOrderId = journey.inboundOrder?.data.id
    const outboundOrderId = journey.outboundOrder?.data.id

    const isConfirmed = await appViewStore.setOpenConfirmDialog(
      true,
      t(
        'journeyWillBeDeletedWarning',
        'The entire Journey and all associated orders will be deleted',
      ),
      t('confirmJourneyDelete', 'Confirm journey delete'),
      hasServiceOrders
        ? t(
            'anyAssociatedStrippingOrStuffingOrdersWillBeDeleted',
            'Any associated stripping/stuffing orders will be deleted',
          )
        : undefined,
    )

    if (!isConfirmed) return

    try {
      const orderIds = []

      if (inboundOrderId && inboundOrderId !== -1) orderIds.push(inboundOrderId)
      if (outboundOrderId && outboundOrderId !== -1) orderIds.push(outboundOrderId)

      await adminActionService.deleteByIds(orderIds, true)

      appViewStore.setShowAlert(
        'success',
        t('successfullyDeletedJourney', 'Successfully deleted journey'),
      )
    } catch (e) {
      appViewStore.setShowAlert('error', t('failedToDeleteJourney', 'Failed to delete journey'))
    }
  }

  const getOptions = () => {
    const journey = editOrderPageViewStore.journey
    if (!journey) return []

    const inboundOrder = journey.inboundOrder?.data
    const outboundOrder = journey.outboundOrder?.data
    const commonOrder = inboundOrder ?? outboundOrder
    const journeyStatus = journey.status

    const actions: ButtonOption[] = [
      {
        label: t('checkInContainer', 'Check in container'),
        disabled:
          (journeyStatus !== ContainerJourneyStatus.Expected &&
            journeyStatus !== ContainerJourneyStatus.Arrived) ||
          !inboundOrder,
        onClick: () => {
          navigateCheckinContainerVisitsPage(inboundOrder!)
        },
      },
      {
        label: t('undoCheckIn', 'Undo check in'),
        disabled:
          journeyStatus === ContainerJourneyStatus.Expected ||
          journeyStatus === ContainerJourneyStatus.Arrived ||
          journeyStatus === ContainerJourneyStatus.Departed ||
          !inboundOrder,
        onClick: async () => {
          if (!inboundOrder) return

          const confirmed = await appViewStore.setOpenConfirmDialog(
            true,
            t(
              'containerWillBeResetToExpected',
              'Container no. {container} will be reset to Expected status and any associated jobs will be terminated. This will afect billing.',
              { container: inboundOrder.containerNumber },
            ),
            t('undoCheckin', 'Undo check in'),
          )

          if (confirmed) {
            adminActionService.undoOrderCheckIn(inboundOrder.id)
          }
        },
      },
      {
        label: t('checkOutContainer', 'Check out container'),
        disabled:
          journeyStatus === ContainerJourneyStatus.Expected ||
          journeyStatus === ContainerJourneyStatus.Arrived ||
          journeyStatus === ContainerJourneyStatus.Departed ||
          !outboundOrder,
        onClick: () => {
          navigateCheckoutContainerPage(outboundOrder!)
        },
      },
      {
        label: t('undoCheckOut', 'Undo check out'),
        disabled: journeyStatus !== ContainerJourneyStatus.Departed || !outboundOrder,
        onClick: async () => {
          if (!outboundOrder) return

          if (!outboundOrder.containerNumber) {
            appViewStore.setShowAlert(
              'error',
              t('containerNumberIsMissing', 'Container number is missing'),
            )
          }

          editOrderPageViewStore.containerYardOperationViewStore.open(
            outboundOrder.containerNumber!,
            outboundOrder.id,
            t('undoCheckout', 'Undo check out'),
            t(
              'containerWillBeResetToTerminal',
              'Container no. {container} will be reset to On terminal status. This will afect billing.',
              { container: outboundOrder.containerNumber },
            ),
            async () => await adminActionService.undoOrderCheckOut(outboundOrder.id),
            journey.data.id,
          )
        },
      },
      {
        label: t('correctYardLocation', 'Correct yard location'),
        disabled: journeyStatus !== ContainerJourneyStatus.OnYard,
        onClick: () => {
          if (commonOrder?.containerNumber) {
            editOrderPageViewStore.containerYardOperationViewStore.open(
              commonOrder.containerNumber,
              commonOrder.id,
              undefined,
              undefined,
              undefined,
              journey.data.id,
            )
          } else {
            appViewStore.setShowAlert(
              'error',
              t('containerNumberIsMissing', 'Container number is missing'),
            )
          }
        },
      },
      {
        label: t('delete', 'Delete'),
        onClick: () => onDeleteJourney(journey),
      },
    ]

    return actions
  }

  return (
    <Stack
      paddingX={theme.customSpacing.l}
      paddingY={theme.customSpacing.s}
      alignItems={'center'}
      direction={'row'}
      sx={{ placeContent: 'space-between' }}
      borderBottom={`1px solid ${theme.palette.divider}`}
    >
      <Stack alignItems={'flex-start'}>
        <Typography variant='h3' align='center'>
          {t('orderDetails', 'Order details')}
        </Typography>
      </Stack>
      {isAdmin && (
        <Stack gap={theme.customSpacing.m} direction='row' alignItems='center'>
          <ButtonWithDropdown label={t('admin', 'Admin')} options={getOptions()} />
        </Stack>
      )}
    </Stack>
  )
})
